import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";

export type SupportCategory = Database["public"]["Enums"]["support_ticket_category"];
export type SupportPriority = Database["public"]["Enums"]["support_ticket_priority"];
export type SupportStatus = Database["public"]["Enums"]["support_ticket_status"];
export type FeedbackType = Database["public"]["Enums"]["feedback_type"];
export type FeedbackStatus = Database["public"]["Enums"]["feedback_status"];
export type AccountRoleContext = Database["public"]["Enums"]["account_role_context"];

export const SUPPORT_CATEGORY_VALUES = [
  "technical_issue",
  "billing_issue",
  "account_issue",
  "login_password_issue",
  "parent_dashboard_issue",
  "lesson_content_issue",
  "family_setup_issue",
  "bug_report",
  "accessibility_issue",
  "feature_request",
  "other",
] as const satisfies readonly SupportCategory[];

export const SUPPORT_PRIORITY_VALUES = ["low", "normal", "high", "urgent"] as const satisfies readonly SupportPriority[];

export const FEEDBACK_TYPE_VALUES = [
  "general_feedback",
  "feature_request",
  "content_suggestion",
  "new_course_request",
  "new_module_request",
  "lesson_improvement_suggestion",
  "confusing_lesson",
  "unclear_vocabulary",
  "ux_ui_suggestion",
  "parent_resource_request",
  "support_content_improvement",
  "other",
] as const satisfies readonly FeedbackType[];

export const FEEDBACK_STATUS_VALUES = ["new", "reviewed", "planned", "completed", "dismissed"] as const satisfies readonly FeedbackStatus[];
export const SUPPORT_STATUS_VALUES = ["submitted", "in_review", "waiting_on_user", "resolved"] as const satisfies readonly SupportStatus[];

export const supportCategoryOptions: Array<{ value: SupportCategory; label: string; description: string }> = [
  { value: "technical_issue", label: "Technical issue", description: "Crashes, loading problems, errors, or broken interactions." },
  { value: "billing_issue", label: "Billing issue", description: "Subscription, invoices, charges, or checkout questions." },
  { value: "account_issue", label: "Account issue", description: "Profile, account setup, permissions, or sign-in state issues." },
  { value: "login_password_issue", label: "Login / password issue", description: "Forgot password, reset links, or access problems." },
  { value: "parent_dashboard_issue", label: "Parent dashboard issue", description: "Household, child progress, or parent workflow issues." },
  { value: "lesson_content_issue", label: "Lesson / content issue", description: "Problem in a lesson, quiz, module, or curriculum experience." },
  { value: "family_setup_issue", label: "Family setup issue", description: "Invites, child accounts, household setup, or family workflows." },
  { value: "bug_report", label: "Bug report", description: "A reproducible product bug you want the team to fix." },
  { value: "accessibility_issue", label: "Accessibility issue", description: "Readability, keyboard access, contrast, or assistive tech issues." },
  { value: "feature_request", label: "Feature request", description: "Something missing that blocks the experience you want." },
  { value: "other", label: "Other", description: "Anything else that does not fit the categories above." },
];

export const supportPriorityOptions: Array<{ value: SupportPriority; label: string; description: string }> = [
  { value: "low", label: "Low", description: "Helpful to fix, but not blocking progress." },
  { value: "normal", label: "Normal", description: "Standard request that needs a review." },
  { value: "high", label: "High", description: "Strongly impacting learning, billing, or account access." },
  { value: "urgent", label: "Urgent", description: "Immediate access or security issue that needs attention." },
];

export const feedbackTypeOptions: Array<{ value: FeedbackType; label: string; description: string }> = [
  { value: "general_feedback", label: "General feedback", description: "What feels strong, weak, or confusing overall." },
  { value: "feature_request", label: "Feature request", description: "A product capability you want HeirWise to build." },
  { value: "content_suggestion", label: "Content suggestion", description: "A topic, lesson, or learning format we should add." },
  { value: "new_course_request", label: "New course request", description: "A full course area you want added to the curriculum." },
  { value: "new_module_request", label: "New module request", description: "A simulation or module you want to learn through." },
  { value: "lesson_improvement_suggestion", label: "Lesson improvement", description: "Something inside a lesson that should be clearer or better." },
  { value: "confusing_lesson", label: "Confusing lesson", description: "A lesson that felt unclear, misleading, or hard to follow." },
  { value: "unclear_vocabulary", label: "Confusing vocabulary", description: "A term or concept that needs better explanation." },
  { value: "ux_ui_suggestion", label: "UX/UI suggestion", description: "A usability or interface improvement suggestion." },
  { value: "parent_resource_request", label: "Parent resource request", description: "Resources or guidance for parents and guardians." },
  { value: "support_content_improvement", label: "Support/help improvement", description: "A suggestion for FAQs, onboarding, or help content." },
  { value: "other", label: "Other", description: "Anything else that should reach the HeirWise team." },
];

export const importanceOptions = [
  { value: 1, label: "Nice to have" },
  { value: 2, label: "Useful" },
  { value: 3, label: "Important" },
  { value: 4, label: "Critical" },
] as const;

export const sentimentOptions = [
  { value: 2, label: "Very positive" },
  { value: 1, label: "Positive" },
  { value: 0, label: "Neutral" },
  { value: -1, label: "Negative" },
  { value: -2, label: "Very negative" },
] as const;

export const ratingOptions = [1, 2, 3, 4, 5] as const;

export const SUPPORT_STATUS_LABELS: Record<SupportStatus, string> = {
  submitted: "Submitted",
  in_review: "In Review",
  waiting_on_user: "Waiting on Response",
  resolved: "Resolved",
};

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  new: "New",
  reviewed: "Reviewed",
  planned: "Planned",
  completed: "Completed",
  dismissed: "Dismissed",
};

export const FAQ_ITEMS = [
  {
    question: "I can’t log in. What should I do first?",
    answer: "Double-check that you’re using the same email you signed up with, then use the Forgot Password flow from the sign-in screen if you need a fresh reset link.",
    keywords: ["login", "password", "sign in", "access"],
  },
  {
    question: "How do I change my email or password?",
    answer: "Open Login & Security from your profile to update your email or password with confirmation and clear status messages.",
    keywords: ["change email", "change password", "security"],
  },
  {
    question: "My password reset link expired.",
    answer: "Request a new reset email from the sign-in screen. For safety, old recovery links stop working after a while.",
    keywords: ["forgot password", "reset link", "expired"],
  },
  {
    question: "How do I set up the family or parent dashboard?",
    answer: "Go to Parent Dashboard to create a household, invite child accounts, and manage household billing and progress tracking.",
    keywords: ["parent", "family", "household", "child account"],
  },
  {
    question: "My billing or subscription looks wrong.",
    answer: "Open your billing controls from Profile or Parent Dashboard. If something still looks off, submit a Billing Help request from Support.",
    keywords: ["billing", "subscription", "invoice", "charge"],
  },
  {
    question: "My lesson progress didn’t update.",
    answer: "Refresh the lesson or dashboard, then check Support if the issue persists. Include the lesson name so the team can investigate faster.",
    keywords: ["progress", "lesson", "quiz", "completion"],
  },
  {
    question: "Why am I not seeing AI features?",
    answer: "Some AI features depend on lesson context or your current workflow. If a tutor or explanation panel is missing unexpectedly, submit a technical request.",
    keywords: ["ai", "tutor", "missing feature", "explanation"],
  },
  {
    question: "A quiz question or lesson explanation seems wrong.",
    answer: "Use Support for bugs that block progress, or Feedback if you want to suggest better phrasing, examples, or new content.",
    keywords: ["content", "question", "wrong", "lesson issue"],
  },
  {
    question: "The app looks broken on my phone.",
    answer: "Submit a Technical Troubleshooting request and include your device type plus a screenshot if possible. That helps the team reproduce the issue quickly.",
    keywords: ["mobile", "layout", "display", "responsive"],
  },
  {
    question: "How do I contact HeirWise directly?",
    answer: "You can email us at founder@steenventures.com or use the in-app Support Center to submit a request. We respond within 24–48 hours.",
    keywords: ["contact", "email", "support", "help", "reach"],
  },
];

// Product support mailbox. Corporate/legal correspondence uses
// founder@steenventures.com on the Terms, Privacy, and legal pages.
export const SUPPORT_EMAIL = "founder@steenventures.com";

export const SUPPORT_ACTIONS = [
  { title: "Contact Support", description: "Reach the team with a complete support request.", category: "technical_issue" as SupportCategory },
  { title: "Report a Bug", description: "Flag something broken or inconsistent.", category: "bug_report" as SupportCategory },
  { title: "Billing Help", description: "Get help with subscriptions, invoices, or charges.", category: "billing_issue" as SupportCategory },
  { title: "Account Help", description: "Fix profile, login, or setup issues.", category: "account_issue" as SupportCategory },
  { title: "Lesson / Content Help", description: "Report lesson, quiz, or curriculum problems.", category: "lesson_content_issue" as SupportCategory },
  { title: "Family / Parent Dashboard Help", description: "Get support for household and child account workflows.", category: "parent_dashboard_issue" as SupportCategory },
  { title: "Technical Troubleshooting", description: "Share device-specific issues and app errors.", category: "technical_issue" as SupportCategory },
  { title: "Send Feedback", description: "Share ideas and suggestions for the product.", feedbackType: "general_feedback" as FeedbackType },
];

export const CONTENT_SUGGESTION_TYPES = new Set<FeedbackType>([
  "content_suggestion",
  "new_course_request",
  "new_module_request",
  "lesson_improvement_suggestion",
  "parent_resource_request",
  "support_content_improvement",
]);

export const supportTicketSchema = z.object({
  category: z.enum(SUPPORT_CATEGORY_VALUES),
  subject: z.string().trim().min(4, "Add a short subject.").max(120, "Keep the subject under 120 characters."),
  message: z.string().trim().min(20, "Share a little more detail so support can help.").max(4000, "Keep the message under 4000 characters."),
  priority: z.enum(SUPPORT_PRIORITY_VALUES),
  courseId: z.string().trim().max(120).optional().or(z.literal("")),
  lessonId: z.string().trim().optional().or(z.literal("")),
  moduleKey: z.string().trim().max(120).optional().or(z.literal("")),
});

export const feedbackSchema = z.object({
  feedbackType: z.enum(FEEDBACK_TYPE_VALUES),
  subject: z.string().trim().min(4, "Add a short title.").max(120, "Keep the title under 120 characters."),
  message: z.string().trim().min(12, "Tell us a bit more so the team can act on it.").max(4000, "Keep the message under 4000 characters."),
  courseId: z.string().trim().max(120).optional().or(z.literal("")),
  lessonId: z.string().trim().optional().or(z.literal("")),
  moduleKey: z.string().trim().max(120).optional().or(z.literal("")),
  rating: z.number().min(1).max(5).nullable().optional(),
  sentiment: z.number().min(-2).max(2).nullable().optional(),
  importanceLevel: z.number().min(1).max(4).nullable().optional(),
});

export const forgotPasswordSchema = z.object({
  email: z.string().trim().email("Enter a valid email address.").max(255, "Email must be under 255 characters."),
});

export const changeEmailSchema = z.object({
  newEmail: z.string().trim().email("Enter a valid email address.").max(255),
  confirmEmail: z.string().trim().email("Confirm your new email address.").max(255),
  currentPassword: z.string().min(6, "Enter your current password.").max(128),
}).refine((value) => value.newEmail === value.confirmEmail, {
  message: "Email confirmation does not match.",
  path: ["confirmEmail"],
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(6, "Enter your current password.").max(128),
  newPassword: z.string()
    .min(8, "Use at least 8 characters.")
    .max(128, "Password must be under 128 characters.")
    .regex(/[a-z]/, "Add a lowercase letter.")
    .regex(/[A-Z]/, "Add an uppercase letter.")
    .regex(/[0-9]/, "Add a number."),
  confirmPassword: z.string().min(8).max(128),
}).refine((value) => value.newPassword === value.confirmPassword, {
  message: "Password confirmation does not match.",
  path: ["confirmPassword"],
}).refine((value) => value.currentPassword !== value.newPassword, {
  message: "Choose a new password that is different from the current one.",
  path: ["newPassword"],
});

export function deriveAccountRoleContext(params: {
  isParent?: boolean;
  householdOwnerId?: string | null;
  userId?: string | null;
}): AccountRoleContext {
  if (params.isParent && params.householdOwnerId && params.userId && params.householdOwnerId === params.userId) {
    return "household_owner";
  }
  if (params.isParent) return "parent";
  if (params.userId) return "learner";
  return "unknown";
}

export function getDeviceType(viewportWidth: number): string {
  if (viewportWidth <= 640) return "mobile";
  if (viewportWidth <= 1024) return "tablet";
  return "desktop";
}

export function getBrowserLabel(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  if (ua.includes("edg")) return "Edge";
  if (ua.includes("chrome") && !ua.includes("edg")) return "Chrome";
  if (ua.includes("safari") && !ua.includes("chrome")) return "Safari";
  if (ua.includes("firefox")) return "Firefox";
  return "Other";
}

export function sanitizeAttachmentName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "-").replace(/-+/g, "-");
}

export function buildReferenceCode(id: string): string {
  return `HW-${id.slice(0, 8).toUpperCase()}`;
}

export function getPasswordStrength(password: string) {
  let score = 0;
  if (password.length >= 8) score += 1;
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;

  if (score <= 2) return { label: "Needs work", score: 35 };
  if (score <= 4) return { label: "Strong", score: 72 };
  return { label: "Excellent", score: 100 };
}

export function matchesFaq(item: { question: string; answer: string; keywords: string[] }, query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [item.question, item.answer, ...item.keywords].some((value) => value.toLowerCase().includes(q));
}
